import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'

export const usePainterWorkOrderStyles = makeStyles<Theme>((theme) => ({
  contactCard: {
    border: `1px solid #E5E7EB`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25)
  },
  contactName: {
    ...theme.typography.h4,
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5)
  },
  contactDetails: {
    ...theme.typography.body2,
    fontSize: theme.typography.pxToRem(14),
    color: theme.paintscout.palette.brand.Neutral.P60,
    lineHeight: 1.4
  },
  summaryRowCollapsed: {
    backgroundColor: `${theme.palette.common.white} !important`,
    border: `1px solid #E5E7EB !important`
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.palette.background.default}`,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.25),
    cursor: 'pointer',
    height: '40px'
  },
  summaryRowExpanded: { border: '1px solid #E5E7EB', borderRadius: '4px 4px 0 0 !important' },
  summaryRowStatic: { cursor: 'default' },
  summaryRowLabel: { ...theme.typography.body1, color: theme.palette.text.primary },
  summaryRowValue: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  summaryRowValueText: {
    ...theme.typography.body1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  summaryRowIcon: { fontSize: 16, color: theme.paintscout.palette.brand.Neutral.P60 },
  summaryRowContent: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P60,
    padding: theme.spacing(2, 1.5),
    backgroundColor: theme.palette.common.white,
    border: '1px solid #E5E7EB',
    borderTop: 'none',
    borderRadius: '0 0 4px 4px',
    marginTop: -10,
    marginBottom: theme.spacing(1.25),
    '& p': {
      margin: '0px'
    }
  },
  workDetailsHeading: {
    ...theme.typography.h4,
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3.5)
  },
  workAreaGrid: {
    border: `1px solid #E5E7EB`,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing(1.25),
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white
  },
  workAreaRow: {
    display: 'flex',
    borderBottom: `1px solid #E5E7EB`,
    '&:last-child': { borderBottom: 'none' },
    minHeight: 56
  },
  workAreaCell: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.5),
    ...theme.typography.body2,
    borderRight: `1px solid #E5E7EB`,
    '&:last-child': { borderRight: 'none' }
  },
  workAreaCellColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.5),
    ...theme.typography.body2,
    borderRight: `1px solid #E5E7EB`,
    '&:last-child': { borderRight: 'none' }
  },
  workAreaCellRow: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  workAreaCellLabel: {
    color: theme.paintscout.palette.brand.Neutral.P60,
    whiteSpace: 'nowrap'
  },
  workAreaCellValue: {
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    fontWeight: theme.typography.fontWeightMedium
  },
  workAreaTotals: {
    display: 'flex',
    backgroundColor: theme.palette.common.white
  },
  workAreaTotal: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(0.5),
    ...theme.typography.body2,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1.25, 1.5),
    '&:not(:last-child)': {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: '-0.5px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 1,
        height: 15,
        backgroundColor: theme.palette.divider
      }
    }
  },
  workAreaTotalDivider: {},
  workItem: {
    border: `1px solid #E5E7EB`,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing(1),
    overflow: 'hidden'
  },
  workItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    backgroundColor: theme.palette.common.white,
    minHeight: 64
  },
  workItemLabel: {
    ...theme.typography.body1,
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium
  },
  workItemRight: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  workItemHours: { ...theme.typography.body2, color: theme.paintscout.palette.brand.Neutral.P60 },
  workItemExpandIcon: { fontSize: 16, color: theme.paintscout.palette.brand.Neutral.P60 },
  workSubareaWrap: { marginBottom: theme.spacing(1.25) },
  workSubareaDivider: {
    width: 56,
    height: 6,
    borderRadius: 999,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0.5, 'auto', 1.25)
  },
  workSubareaCard: {
    '&:not(:last-child)': {
      paddingBottom: theme.spacing(1.25)
    }
  },
  workSubareaCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1)
  },
  workSubareaName: {
    ...theme.typography.body1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  workSubareaHours: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P60,
    whiteSpace: 'nowrap'
  },
  workSubareaDetail: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.paintscout.palette.brand.Neutral.P60,
    marginTop: theme.spacing(0.5)
  },
  noWorkOrderBanner: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6)
  },
  tabEmptyState: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P60,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing(2)
  },
  tabCard: {
    border: `1px solid #E5E7EB`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25)
  },
  tabCardButton: {
    width: '100%',
    textAlign: 'left',
    background: theme.palette.common.white,
    cursor: 'pointer'
  },
  tabCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  tabCardTitle: {
    ...theme.typography.body1,
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium
  },
  tabCardMeta: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P60,
    textAlign: 'right'
  },
  tabCardBody: {
    ...theme.typography.body2,
    color: theme.paintscout.palette.brand.Neutral.P60,
    lineHeight: 1.5
  },
  tabCardLink: {
    ...theme.typography.body2,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: theme.typography.fontWeightMedium
  },
  tabActionButton: {
    width: '100%',
    borderRadius: theme.borderRadius.md,
    border: `1px solid #E5E7EB`,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.25),
    textAlign: 'left',
    cursor: 'pointer',
    ...theme.typography.body1,
    color: theme.palette.text.primary
  },
  fullWorkOrderButton: {
    width: '100%',
    borderRadius: theme.borderRadius.md,
    border: `1px solid #E5E7EB`,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.25),
    textAlign: 'left',
    cursor: 'pointer',
    ...theme.typography.body1,
    color: theme.palette.text.primary
  }
}))
